import { workbenchStore } from '../stores/workbench';
import {
  getActiveTemplate,
  getHeadlessTemplateFiles,
  setActiveTemplate,
  type TemplateId,
} from '../templates/headlessTemplate';

/** Preview iframe ↔ host protocol (must stay in snapshot zip / editor / Chef context). */
export const HOT_EDIT_BRIDGE_REL_PATHS = [
  'src/ideoz/previewBridge.ts',
  'src/ideoz/previewBridgeTypes.ts',
  'src/ideoz/renderSignal.ts',
] as const;

/**
 * Sorted union of headless template paths and workspace store paths (store wins on content).
 */
export function getMergedFilePaths(): string[] {
  const template = getHeadlessTemplateFiles();
  const store = workbenchStore.files.get();
  const set = new Set<string>([...Object.keys(template), ...Object.keys(store)]);
  return Array.from(set).sort((a, b) => a.localeCompare(b));
}

/**
 * Resolved file body: live store entry overrides template defaults.
 */
export function getMergedFileContent(path: string): string {
  const fromStore = workbenchStore.files.get()[path];
  if (fromStore) return fromStore.content;
  return getHeadlessTemplateFiles()[path] ?? '';
}

/**
 * Template merged with the store as a plain map — what the light preview compiles (F2). The
 * store holds only generated and hand-edited files, so the template supplies the rest (the ui
 * components, `src/lib/utils.ts`, `index.css`, the entry).
 *
 * The preview bridge is the exception: the template's copy always wins. It is how the page
 * talks to the panel, so a prototype that carries its own (a builder repair round once wrote a
 * no-op `initPreviewBridge` into one) left the panel waiting for a page that never said hello,
 * and a finished version read "No saved version of this prototype yet". The WebContainer path
 * gets the same result by writing the template's bridge back after every restore
 * (`ensureHotEditBridgeFiles`).
 */
export function getMergedPreviewFiles(): Record<string, string> {
  const template = getHeadlessTemplateFiles();
  const merged: Record<string, string> = { ...template };
  for (const [path, file] of Object.entries(workbenchStore.files.get())) {
    merged[path] = file.content;
  }
  for (const path of HOT_EDIT_BRIDGE_REL_PATHS) {
    if (template[path] !== undefined) merged[path] = template[path];
  }
  return merged;
}

/**
 * Copy template-only paths into the store so editor + sync behave uniformly.
 */
export function materializeTemplateFileIfNeeded(path: string): void {
  if (workbenchStore.files.get()[path]) return;
  const t = getHeadlessTemplateFiles()[path];
  if (t === undefined) return;
  workbenchStore.files.setKey(path, { content: t, modified: false });
  workbenchStore.recordToolWriteSnapshot(path, t);
}

/**
 * Switches the workspace to `next` and drops the store's untouched copies of the previous
 * template's own files.
 *
 * Opening a template file in the editor copies it into the store, and the store wins over the
 * template. So a package.json opened while the workspace was still web kept listing Radix on a
 * mobile prototype after the switch. A copy the founder edited (`modified`) is kept, and so is a
 * file both templates share, which is the same either way.
 */
export function switchTemplate(next: TemplateId): void {
  const previous = getActiveTemplate();
  if (previous === next) return;
  const before = getHeadlessTemplateFiles(previous);
  const after = getHeadlessTemplateFiles(next);
  setActiveTemplate(next);
  for (const [path, entry] of Object.entries(workbenchStore.files.get())) {
    if (entry.modified || before[path] === undefined) continue;
    if (entry.content === before[path] && after[path] !== before[path]) {
      workbenchStore.removeFile(path);
    }
  }
}

/** Ensures bridge modules exist in the nanostore so snapshots, zip, and search treat them like user files. */
export function materializeHotEditBridgeFilesInStore(): void {
  for (const p of HOT_EDIT_BRIDGE_REL_PATHS) {
    materializeTemplateFileIfNeeded(p);
  }
}
